import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useT } from "../i18n";

const ABOUT_SLIDES = [
  {
    image:
      "https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/gprc9dgj_fundadores.jpeg",
    alt: "Fundadores da Yin Motion",
    position: "object-[62%_52%]",
    labelKey: "quemSomos.foundersLabel",
  },
  {
    image:
      "https://customer-assets-0z36b82j.emergentagent.net/job_movimento-saude-1/artifacts/6ju2i446_equipa.jpeg",
    alt: "Equipa da Yin Motion",
    position: "object-[center_48%]",
    labelKey: "quemSomos.teamLabel",
  },
];

const fade = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.75, ease: "easeOut" },
};

export const QuemSomos = () => {
  const { t } = useT();
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setSlide((s) => (s + 1) % ABOUT_SLIDES.length),
      4000
    );
    return () => clearInterval(id);
  }, []);
  return (
    <section
      id="quem-somos"
      data-testid="about-section"
      className="relative py-28 md:py-44"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* ═══════ LEFT COLUMN ═══════ */}
        <motion.div {...fade} className="lg:col-span-6">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-9">
            <span className="h-px w-10 bg-[#A3BD98]" />
            <span className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#ADBDAD]">
              {t("quemSomos.eyebrow")}
            </span>
          </div>

          {/* Headline — 3 controlled lines */}
          <h2 className="font-serif text-[32px] md:text-[38px] lg:text-[42px] xl:text-[46px] leading-[1.18] tracking-tight text-[#2A322C] font-light max-w-[640px]">
            <span className="block font-bold">{t("quemSomos.headline1")}</span>
            <span className="block">
              {t("quemSomos.headline2Prefix")}
              <span className="font-normal text-[#A3BD98]">
                {t("quemSomos.headline2Word")}
              </span>
            </span>
            <span className="block font-bold">{t("quemSomos.headline3")}</span>
          </h2>

          {/* Founders / Team slideshow — alternates every 4s */}
          <div className="mt-10 lg:mt-12">
            <div
              data-testid="about-slideshow"
              className="relative aspect-[4/5] overflow-hidden rounded-[14px] shadow-[0_14px_45px_-18px_rgba(42,50,44,0.18)]"
            >
              {ABOUT_SLIDES.map((s, i) => (
                <img
                  key={s.image}
                  src={s.image}
                  alt={s.alt}
                  className={`absolute inset-0 w-full h-full object-cover ${s.position} transition-opacity duration-1000 ease-in-out ${
                    i === slide ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <span className="h-px w-8 bg-[#A3BD98]" />
              <span
                data-testid="about-slideshow-label"
                className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#5C6660] transition-opacity duration-500"
              >
                {t(ABOUT_SLIDES[slide].labelKey)}
              </span>
            </div>
          </div>
        </motion.div>

        {/* ═══════ RIGHT COLUMN ═══════ */}
        <motion.div
          {...fade}
          transition={{ duration: 0.75, ease: "easeOut", delay: 0.15 }}
          className="lg:col-span-6 flex flex-col gap-16 lg:gap-20 lg:pt-4"
        >
          {/* Description */}
          <div className="space-y-7 text-[#5C6660] text-[17px] md:text-[18px] leading-[1.9] max-w-[520px]">
            <p>
              {t("quemSomos.p1Start")}
              <span className="text-[#2A322C] font-bold">
                {t("quemSomos.p1Bold")}
              </span>
              {t("quemSomos.p1Rest")}
            </p>
            <p>{t("quemSomos.p2")}</p>
          </div>

          {/* Quote */}
          <figure className="relative max-w-[560px] pl-7 md:pl-9">
            <span
              aria-hidden
              className="absolute left-0 top-1 bottom-1 w-px bg-gradient-to-b from-transparent via-[#A3BD98]/70 to-transparent"
            />
            <blockquote className="font-serif italic text-[28px] md:text-[34px] leading-[1.35] text-[#5C6660]/90 tracking-tight">
              <span
                aria-hidden
                className="not-italic text-[#A3BD98] font-bold text-[44px] md:text-[52px] leading-none align-middle mr-2"
              >
                “
              </span>
              {t("quemSomos.quote")}
              <span
                aria-hidden
                className="not-italic text-[#A3BD98] font-bold text-[44px] md:text-[52px] leading-none align-middle ml-1"
              >
                ”
              </span>
            </blockquote>
          </figure>

          {/* Subtle separator */}
          <span
            aria-hidden
            className="h-px w-full bg-[#2A322C]/10 -my-4"
          />

          {/* Editorial stats — clean, fluid list inside subtle container */}
          <div className="relative mt-auto rounded-[14px] bg-[#EEF2E9]/20 border border-[#A3BD98]/10 px-7 py-8 md:px-10 md:py-10 shadow-[0_6px_24px_-18px_rgba(42,50,44,0.12)]">
            <ul className="flex flex-col gap-5 md:gap-7 text-[#2A322C] text-center lg:text-left">
              {t("quemSomos.stats").map((item, i) => (
                <li
                  key={i}
                  data-testid={`about-stat-${i}`}
                  className="font-serif text-[20px] md:text-[24px] lg:text-[26px] leading-[1.3] tracking-tight font-light"
                >
                  <span className="font-semibold text-[#2A322C] mr-2">
                    {item.strong}
                  </span>
                  <span className="font-light text-[#5C6660]/90">
                    {item.rest}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
